/**
 * bulk-update — de logica achter 'Bulk bijwerken' in de boringentab (Trello BCwaRl2Z).
 *
 * Na het veldwerk meet een landmeter de boringen in (Trimble), en datum en boormeester worden
 * achteraf aangevuld. Dit bestand doet alles wat daar NIET het scherm voor nodig heeft, zodat
 * het zonder browser te testen is:
 *   - een csv lezen (`;` of `,`, decimale komma, `Job:`-regels van een veldlijst overslaan)
 *   - herkennen of de eerste regel kopteksten zijn, en of het ons eigen template is
 *   - kolommen raden op inhoud: welke kolom de boringnaam is, welke X/Y/Z
 *   - het stelsel herkennen (RD of WGS84, per bestand, niet per regel)
 *   - namen koppelen: exact, of genormaliseerd ('B1' = 'B01' = 'b-1')
 *   - het voorbeeld (oud → nieuw) en de aanvraag voor de backend opbouwen
 *
 * Opslag is altijd WGS84 (x = lon, y = lat); `projects.coordinate_system` bepaalt alleen de
 * weergave. We rekenen dus alles om naar WGS84, nooit terug.
 *
 * Een lege cel betekent "laten staan": dat veld gaat niet mee in de aanvraag. Wissen via de
 * import kan niet.
 */
import { convertToWgs84, convertFromWgs84, COORDINATE_SYSTEMS } from '@mv/models';

/** De zes velden die je kunt bijwerken, in de volgorde van het template. */
export const BULK_FIELDS = ['name', 'x', 'y', 'z', 'datum', 'boormeester'];

/** Wat er in de dropdown 'Koppelen aan' staat. '' = negeren. */
export function fieldLabel(field, system) {
  const sys = COORDINATE_SYSTEMS[system];
  const axis = (i, fallback) => (sys ? `${sys.axes[i]}-coördinaat (${sys.id})` : fallback);
  return {
    name: 'Boornummer (sleutel)',
    x: axis(0, 'X-coördinaat'),
    y: axis(1, 'Y-coördinaat'),
    z: 'Maaiveldhoogte (m NAP)',
    datum: 'Datum boring',
    boormeester: 'Boormeester',
    '': 'Niet gebruiken',
  }[field];
}

/**
 * Kopteksten van het template. De coördinaatkolommen heten naar het stelsel van het project
 * ('X (RD)', 'Lon (WGS84)'), zodat de gebruiker weet wat hij moet invullen.
 */
export function templateHeaders(system = 'RD') {
  const sys = COORDINATE_SYSTEMS[system] || COORDINATE_SYSTEMS.RD;
  return ['Boornummer', `${sys.axes[0]} (${sys.id})`, `${sys.axes[1]} (${sys.id})`,
    'Maaiveldhoogte (m NAP)', 'Datum boring', 'Boormeester'];
}

/** Template als rijen (kop + één regel per boring), klaar voor SheetJS `aoa_to_sheet`. */
export function templateRows(borings, system) {
  const names = borings.map(b => b.name || '').filter(Boolean)
    .sort((a, b) => a.localeCompare(b, 'nl', { numeric: true, sensitivity: 'base' }));
  return [templateHeaders(system), ...names.map(n => [n, '', '', '', '', ''])];
}

// ── Lezen ────────────────────────────────────────────────────────────────────

/** `Job:60260003 Zutphen,Version:26.10,…` → '60260003'. */
const JOB_RE = /^\s*job\s*:\s*([^\s,;]+)/i;

/** Eén csv-regel in cellen, met aanhalingstekens ("a;b" blijft één cel). */
function splitCsvLine(line, sep) {
  const out = [];
  let cell = '';
  let quoted = false;
  for (let i = 0; i < line.length; i++) {
    const c = line[i];
    if (quoted) {
      if (c === '"' && line[i + 1] === '"') { cell += '"'; i++; }
      else if (c === '"') quoted = false;
      else cell += c;
    } else if (c === '"') quoted = true;
    else if (c === sep) { out.push(cell); cell = ''; }
    else cell += c;
  }
  out.push(cell);
  return out.map(s => s.trim());
}

/**
 * Scheidingsteken raden. `;` wint als hij voorkomt: dat is wat Nederlandse Excel schrijft, en
 * dan staat er vaak een decimale komma in de getallen (`4,12`).
 */
function guessSeparator(lines) {
  const sample = lines.slice(0, 20).join('\n');
  if (sample.includes(';')) return ';';
  if (sample.includes('\t')) return '\t';
  return ',';
}

/**
 * Csv-tekst → rijen. Regels die met `Job:` beginnen (veldlijst uit de Trimble) worden
 * overgeslagen; de jobcode bewaren we voor een waarschuwing als hij niet bij het project past.
 *
 * @returns {{ rows: string[][], jobCode: string|null }}
 */
export function parseCsv(text) {
  const lines = String(text || '').replace(/^﻿/, '').split(/\r\n|\r|\n/).filter(l => l.trim());
  let jobCode = null;
  const data = [];
  for (const l of lines) {
    const m = l.match(JOB_RE);
    if (m) { jobCode = jobCode || m[1]; continue; }
    data.push(l);
  }
  const sep = guessSeparator(data);
  return { rows: data.map(l => splitCsvLine(l, sep)), jobCode };
}

/** Een cel uit SheetJS of csv als getal; '' → null, geen getal → NaN. */
export function parseNumber(v) {
  if (v == null) return null;
  if (typeof v === 'number') return Number.isFinite(v) ? v : NaN;
  let s = String(v).trim().replace(/\s/g, '');
  if (!s) return null;
  const comma = s.lastIndexOf(',');
  const dot = s.lastIndexOf('.');
  if (comma > -1 && dot > -1) {
    // Beide: het laatste teken is de decimaal, het andere scheidt duizendtallen.
    s = comma > dot ? s.replace(/\./g, '').replace(',', '.') : s.replace(/,/g, '');
  } else if (comma > -1) {
    s = s.replace(',', '.');
  }
  return /^[-+]?\d*\.?\d+(e[-+]?\d+)?$/i.test(s) ? Number(s) : NaN;
}

const isEmpty = (v) => v == null || String(v).trim() === '';
const pad = (n) => String(n).padStart(2, '0');

function isoIfValid(y, m, d) {
  const iso = `${y}-${pad(m)}-${pad(d)}`;
  const t = new Date(`${iso}T00:00:00Z`);
  return !Number.isNaN(t.getTime()) && t.toISOString().slice(0, 10) === iso ? iso : NaN;
}

/**
 * Een datumcel → `YYYY-MM-DD` (zo staat `borings.datum` opgeslagen, net als het formulier
 * 'Nieuwe boring'). Begrijpt `dd-mm-jjjj` (ook met / of .), ISO, en Excel-datumgetallen.
 * '' → null, onleesbaar → NaN.
 */
export function parseDate(v) {
  if (isEmpty(v)) return null;
  if (v instanceof Date) {
    return Number.isNaN(v.getTime()) ? NaN : isoIfValid(v.getFullYear(), v.getMonth() + 1, v.getDate());
  }
  if (typeof v === 'number') {
    // Excel-serienummer: dagen sinds 30-12-1899. Alleen een redelijk bereik (1955–2119).
    if (v < 20000 || v > 80000) return NaN;
    const d = new Date(Date.UTC(1899, 11, 30) + Math.floor(v) * 86400000);
    return d.toISOString().slice(0, 10);
  }
  const s = String(v).trim();
  let m = s.match(/^(\d{1,2})[-/.](\d{1,2})[-/.](\d{4})$/);
  if (m) return isoIfValid(m[3], +m[2], +m[1]);
  m = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})(?:[T ].*)?$/);
  if (m) return isoIfValid(m[1], +m[2], +m[3]);
  // Een getal als tekst (csv uit Excel met een datumcel als getal opgemaakt).
  if (/^\d{5}$/.test(s)) return parseDate(Number(s));
  return NaN;
}

/** `2026-10-13` → `13-10-2026`, zoals het template en de boringentab hem tonen. */
export function formatDateNl(iso) {
  const m = String(iso || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[3]}-${m[2]}-${m[1]}` : (iso || '');
}

// ── Kopteksten ───────────────────────────────────────────────────────────────

/** Koptekst vergelijkbaar maken: kleine letters, alleen letters/cijfers. */
const headerKey = (h) => String(h ?? '').toLowerCase().replace(/[^a-z0-9]/g, '');
/** Zonder toevoeging tussen haakjes: 'X (RD)' → 'x', 'Maaiveldhoogte (m NAP)' → 'maaiveldhoogte'. */
const headerBase = (h) => headerKey(String(h ?? '').replace(/\(.*?\)/g, ''));

const HEADER_ALIASES = {
  name: ['boornummer', 'boring', 'boringnaam', 'boornaam', 'naam', 'name', 'puntnaam', 'boorpunt', 'code'],
  x: ['x', 'xrd', 'xcoordinaat', 'lon', 'lng', 'longitude', 'lengtegraad', 'easting', 'oost'],
  y: ['y', 'yrd', 'ycoordinaat', 'lat', 'latitude', 'breedtegraad', 'northing', 'noord'],
  z: ['z', 'maaiveldhoogte', 'maaiveld', 'mv', 'mvnap', 'hoogte', 'zmv', 'nap'],
  datum: ['datum', 'datumboring', 'boordatum', 'date'],
  boormeester: ['boormeester', 'boorder', 'driller', 'uitvoerder'],
};

function aliasField(header) {
  const base = headerBase(header);
  if (!base) return '';
  for (const [field, list] of Object.entries(HEADER_ALIASES)) {
    if (list.includes(base)) return field;
  }
  return '';
}

/** Is deze kopregel ons eigen template? Geeft dan het stelsel uit de kolomnamen terug. */
function templateSystem(header) {
  const got = header.map(headerKey).join('|');
  for (const id of Object.keys(COORDINATE_SYSTEMS)) {
    if (templateHeaders(id).map(headerKey).join('|') === got) return id;
  }
  return null;
}

const isNumericCell = (v) => { const n = parseNumber(v); return n != null && !Number.isNaN(n); };

/**
 * Rijen uit een bestand → kop + gegevens. Een kopregel herkennen we aan: geen enkel getal erin,
 * én ofwel een bekende kolomnaam, ofwel getallen in de regels eronder. Een veldlijst zonder kop
 * (`390,202198.970,466008.899,8.101,b52`) heeft getallen in regel één, dus die blijft data.
 *
 * @param {any[][]} rawRows
 * @returns {{ header: string[]|null, rows: any[][], columns: string[], jobCode: string|null, templateSystem: string|null }}
 */
export function readTable(rawRows, jobCode = null) {
  const rows = [];
  for (const r of rawRows || []) {
    const cells = (r || []).map(c => (typeof c === 'string' ? c.trim() : c));
    if (cells.every(isEmpty)) continue;
    const m = typeof cells[0] === 'string' && cells[0].match(JOB_RE);
    if (m) { jobCode = jobCode || m[1]; continue; }
    rows.push(cells);
  }
  const width = Math.max(0, ...rows.map(r => r.length));
  const first = rows[0] || [];
  const firstHasNumber = first.some(isNumericCell);
  const laterHasNumber = rows.slice(1, 20).some(r => r.some(isNumericCell));
  const hasHeader = rows.length > 0 && !firstHasNumber
    && (first.some(h => aliasField(h)) || laterHasNumber);
  const header = hasHeader ? Array.from({ length: width }, (_, i) => String(first[i] ?? '').trim()) : null;
  const data = (hasHeader ? rows.slice(1) : rows).map(r => Array.from({ length: width }, (_, i) => r[i] ?? ''));
  const columns = Array.from({ length: width }, (_, i) => (header?.[i] || `Kolom ${i + 1}`));
  return { header, rows: data, columns, jobCode, templateSystem: header ? templateSystem(header) : null };
}

// ── Namen koppelen ───────────────────────────────────────────────────────────

/**
 * Genormaliseerde boringnaam: kleine letters, zonder spaties/-/_, en zonder voorloopnullen in
 * elk getalblok. `B01`, `B1`, `b-1` en `B 01` worden allemaal `b1`. Het voorvoegsel blijft
 * onderscheidend: `PB1` ≠ `B1`.
 */
export function normalizeBoringName(name) {
  return String(name ?? '').toLowerCase().trim()
    .replace(/[\s\-_]+/g, '')
    .replace(/\d+/g, (m) => m.replace(/^0+(?=\d)/, ''));
}

export function buildNameIndex(borings) {
  const exact = new Map();
  const normalized = new Map();
  const push = (map, key, b) => { if (!key) return; if (!map.has(key)) map.set(key, []); map.get(key).push(b); };
  for (const b of borings || []) {
    push(exact, String(b.name ?? '').toLowerCase().trim(), b);
    push(normalized, normalizeBoringName(b.name), b);
  }
  return { exact, normalized };
}

/**
 * Welke boring hoort bij deze naam uit het bestand?
 *   exact       → één boring met precies deze naam (hoofdletters/spaties terzijde)
 *   normalized  → één boring na normaliseren ('herkend als B01')
 *   ambiguous   → meerdere kandidaten (project heeft B1 én B01): de gebruiker kiest
 *   none        → geen boring; wordt alleen getoond, nooit aangemaakt
 */
export function matchName(index, value) {
  const raw = String(value ?? '').trim();
  if (!raw) return { kind: 'none', borings: [] };
  const ex = index.exact.get(raw.toLowerCase()) || [];
  if (ex.length === 1) return { kind: 'exact', borings: ex };
  if (ex.length > 1) return { kind: 'ambiguous', borings: ex };
  const nm = index.normalized.get(normalizeBoringName(raw)) || [];
  if (nm.length === 1) return { kind: 'normalized', borings: nm };
  if (nm.length > 1) return { kind: 'ambiguous', borings: nm };
  return { kind: 'none', borings: [] };
}

// ── Stelsel herkennen ────────────────────────────────────────────────────────

const RANGES = {
  RD: (x, y) => x >= 0 && x <= 300000 && y >= 289000 && y <= 629000,
  WGS84: (x, y) => x >= 3 && x <= 8 && y >= 50 && y <= 54,
  WGS84_SWAPPED: (x, y) => x >= 50 && x <= 54 && y >= 3 && y <= 8,
};

/** Deel van de punten dat binnen een bereik valt; lege paren tellen niet mee. */
function fitShare(pairs, test) {
  const usable = pairs.filter(([x, y]) => Number.isFinite(x) && Number.isFinite(y));
  if (!usable.length) return 0;
  return usable.filter(([x, y]) => test(x, y)).length / usable.length;
}

/** Minstens zoveel van de punten moet passen; één tikfout mag de herkenning niet slopen. */
const FIT_THRESHOLD = 0.8;

/**
 * Stelsel van het hele bestand. RD: X 0–300 000, Y 289 000–629 000. WGS84: 3–8 / 50–54; staan
 * lat en lon andersom, dan `swapped` (zonder melding omdraaien). Anders `system: null` en kiest
 * de gebruiker zelf.
 *
 * @param {Array<[number, number]>} pairs  [kolom X, kolom Y] per regel
 * @returns {{ system: 'RD'|'WGS84'|null, swapped: boolean }}
 */
export function detectCoordinateSystem(pairs) {
  if (fitShare(pairs, RANGES.RD) >= FIT_THRESHOLD) return { system: 'RD', swapped: false };
  if (fitShare(pairs, RANGES.WGS84) >= FIT_THRESHOLD) return { system: 'WGS84', swapped: false };
  if (fitShare(pairs, RANGES.WGS84_SWAPPED) >= FIT_THRESHOLD) return { system: 'WGS84', swapped: true };
  return { system: null, swapped: false };
}

/** Past dit ene punt in het herkende stelsel? Voor de regel met een tikfout. */
export function fitsSystem(system, swapped, x, y) {
  if (system === 'RD') return RANGES.RD(x, y);
  if (system === 'WGS84') return swapped ? RANGES.WGS84_SWAPPED(x, y) : RANGES.WGS84(x, y);
  return true;
}

// ── Kolommen koppelen ────────────────────────────────────────────────────────

const columnValues = (rows, i) => rows.map(r => r[i]).filter(v => !isEmpty(v));

/**
 * Welke kolom is wat? Eerst op koptekst (template of bekende naam), daarna op inhoud voor wat
 * er nog open is:
 *   - sleutel: de kolom waarvan de meeste waarden een boringnaam opleveren
 *   - X/Y: het paar getalkolommen dat het best in RD of WGS84 past
 *   - Z: de getalkolom direct na Y, of anders een kolom met decimalen in een redelijk bereik
 *   - datum: een kolom waarvan vrijwel alles een datum is
 * Boormeester raden we niet op inhoud: vrije tekst is niet van een naam te onderscheiden.
 *
 * @returns {string[]} per kolom een veld uit BULK_FIELDS, of '' (negeren)
 */
export function guessMapping(table, borings) {
  const width = table.columns.length;
  const mapping = Array(width).fill('');
  const taken = new Set();
  const assign = (i, f) => { mapping[i] = f; taken.add(f); };

  if (table.header) {
    table.header.forEach((h, i) => {
      const f = aliasField(h);
      if (f && !taken.has(f)) assign(i, f);
    });
  }
  const free = () => mapping.map((f, i) => (f ? -1 : i)).filter(i => i >= 0);

  if (!taken.has('name')) {
    const index = buildNameIndex(borings);
    let best = -1, bestHits = 0;
    for (const i of free()) {
      const hits = columnValues(table.rows, i).filter(v => matchName(index, v).kind !== 'none').length;
      if (hits > bestHits) { best = i; bestHits = hits; }
    }
    if (best >= 0) assign(best, 'name');
  }

  const numeric = free().filter(i => {
    const vals = columnValues(table.rows, i);
    return vals.length && vals.filter(isNumericCell).length / vals.length >= FIT_THRESHOLD;
  });
  const nums = (i) => table.rows.map(r => parseNumber(r[i]));

  if (!taken.has('x') && !taken.has('y')) {
    let bestPair = null, bestScore = 0;
    for (const a of numeric) {
      for (const b of numeric) {
        if (a === b) continue;
        const xs = nums(a), ys = nums(b);
        const pairs = xs.map((x, k) => [x, ys[k]]);
        const det = detectCoordinateSystem(pairs);
        if (!det.system || det.swapped) continue; // omgedraaid WGS84 vangen we via de andere volgorde
        // Bij gelijke stand wint een aangrenzend paar (X staat vrijwel altijd vlak voor Y), en
        // een X met decimalen: een puntnummer (390, 391, …) past ook in het RD-bereik.
        const decimals = xs.some(x => x != null && Number.isFinite(x) && !Number.isInteger(x));
        const score = fitShare(pairs, RANGES[det.system]) + (b === a + 1 ? 0.001 : 0) + (decimals ? 0.0005 : 0);
        if (score > bestScore) { bestScore = score; bestPair = [a, b]; }
      }
    }
    if (bestPair) { assign(bestPair[0], 'x'); assign(bestPair[1], 'y'); }
  }

  if (!taken.has('z')) {
    const yCol = mapping.indexOf('y');
    const plausible = (i) => {
      const vals = nums(i).filter(v => v != null && Number.isFinite(v));
      return vals.length && vals.every(v => v > -50 && v < 500);
    };
    const hasDecimals = (i) => nums(i).some(v => v != null && Number.isFinite(v) && !Number.isInteger(v));
    const cands = numeric.filter(i => !mapping[i] && plausible(i));
    const next = cands.find(i => yCol >= 0 && i === yCol + 1);
    const pick = next ?? cands.find(hasDecimals);
    if (pick != null) assign(pick, 'z');
  }

  if (!taken.has('datum')) {
    for (const i of free()) {
      const vals = columnValues(table.rows, i).filter(v => typeof v !== 'number');
      if (vals.length && vals.filter(v => typeof parseDate(v) === 'string').length / vals.length >= FIT_THRESHOLD) {
        assign(i, 'datum');
        break;
      }
    }
  }
  return mapping;
}

/**
 * Stelsel uit de gekoppelde X/Y-kolommen. `auto` = herkend op inhoud; dan keuren we ook elk
 * los punt op het bereik (tikfout). Lukt herkennen niet, dan geldt het stelsel uit de
 * kopteksten van ons template (bijv. een UTM-project), en anders kiest de gebruiker.
 *
 * @returns {{ system: string|null, swapped: boolean, auto: boolean }}
 */
export function detectFromMapping(table, mapping) {
  const xi = mapping.indexOf('x');
  const yi = mapping.indexOf('y');
  if (xi < 0 || yi < 0) return { system: null, swapped: false, auto: false };
  const det = detectCoordinateSystem(table.rows.map(r => [parseNumber(r[xi]), parseNumber(r[yi])]));
  if (det.system) return { ...det, auto: true };
  return { system: table.templateSystem || null, swapped: false, auto: false };
}

// ── Voorbeeld en aanvraag ────────────────────────────────────────────────────

/** Coördinaat (WGS84) zoals hij in het project getoond wordt. */
export function formatCoordinate(lon, lat, system) {
  if (lon == null || lat == null) return '—';
  const sys = COORDINATE_SYSTEMS[system] || COORDINATE_SYSTEMS.WGS84;
  const p = convertFromWgs84(Number(lon), Number(lat), sys.id);
  if (!p) return '—';
  return `${p.x.toFixed(sys.decimals)} / ${p.y.toFixed(sys.decimals)}`;
}

const sameNumber = (a, b, eps) => a != null && b != null && Math.abs(Number(a) - Number(b)) <= eps;

/**
 * Alles wat het voorbeeld en de knop nodig hebben.
 *
 * @param {object} p
 * @param {{ rows: any[][] }} p.table
 * @param {string[]} p.mapping
 * @param {{ system: string|null, swapped: boolean, auto?: boolean }} p.coords
 * @param {object[]} p.borings          actieve boringen van het project
 * @param {Record<number,string>} [p.rowBoring]  keuze bij een dubbele naam in het project
 * @param {Record<string,number>} [p.dupPick]    gekozen regel per boring die vaker in het bestand staat
 */
export function buildPlan({ table, mapping, coords, borings, rowBoring = {}, dupPick = {} }) {
  const col = (f) => mapping.indexOf(f);
  const nameCol = col('name');
  const index = buildNameIndex(borings);
  const byId = new Map(borings.map(b => [b.id, b]));

  const rows = [];
  if (nameCol >= 0) {
    table.rows.forEach((cells, rowIndex) => {
      const key = String(cells[nameCol] ?? '').trim();
      if (!key) return;
      const match = matchName(index, key);
      const values = {};
      const errors = [];

      const xi = col('x'), yi = col('y');
      if (xi >= 0 || yi >= 0) {
        const px = xi >= 0 ? parseNumber(cells[xi]) : null;
        const py = yi >= 0 ? parseNumber(cells[yi]) : null;
        if (px != null || py != null) {
          if (px == null || py == null) errors.push(px == null ? 'Y zonder X' : 'X zonder Y');
          else if (Number.isNaN(px) || Number.isNaN(py)) errors.push('Coördinaat is geen getal');
          else if (!coords.system) errors.push('Stelsel niet gekozen');
          else if (coords.auto && !fitsSystem(coords.system, coords.swapped, px, py)) errors.push(`Coördinaat valt buiten ${coords.system}`);
          else {
            const [a, b] = coords.swapped ? [py, px] : [px, py];
            const wgs = convertToWgs84(a, b, coords.system);
            if (!wgs) errors.push('Coördinaat niet om te rekenen');
            else { values.x = wgs.longitude; values.y = wgs.latitude; }
          }
        }
      }
      const zi = col('z');
      if (zi >= 0) {
        const z = parseNumber(cells[zi]);
        if (Number.isNaN(z)) errors.push('Maaiveldhoogte is geen getal');
        else if (z != null) values.z = z;
      }
      const di = col('datum');
      if (di >= 0) {
        const d = parseDate(cells[di]);
        if (Number.isNaN(d)) errors.push('Datum niet herkend');
        else if (d) values.datum = d;
      }
      const bi = col('boormeester');
      if (bi >= 0 && !isEmpty(cells[bi])) values.boormeester = String(cells[bi]).trim();

      let boringId = null;
      if (match.kind === 'exact' || match.kind === 'normalized') boringId = match.borings[0].id;
      else if (match.kind === 'ambiguous' && match.borings.some(b => b.id === rowBoring[rowIndex])) boringId = rowBoring[rowIndex];
      rows.push({ rowIndex, key, match, values, errors, boringId });
    });
  }

  // Meerdere regels voor één boring (b39 = punt 403 én 404): pas na een keuze.
  const byBoring = new Map();
  for (const r of rows) {
    if (!r.boringId) continue;
    if (!byBoring.has(r.boringId)) byBoring.set(r.boringId, []);
    byBoring.get(r.boringId).push(r);
  }
  const duplicates = [];
  const active = [];
  for (const [boringId, group] of byBoring) {
    if (group.length === 1) { active.push(group[0]); continue; }
    const chosen = group.find(r => r.rowIndex === dupPick[boringId]);
    duplicates.push({ boring: byId.get(boringId), rows: group, chosen: chosen?.rowIndex ?? null });
    if (chosen) active.push(chosen);
  }

  const entries = active.map((r) => {
    const boring = byId.get(r.boringId);
    const v = r.values;
    const changes = [];
    const patch = {};
    if (v.x != null && !(sameNumber(boring.x, v.x, 1e-9) && sameNumber(boring.y, v.y, 1e-9))) {
      changes.push({ field: 'xy', from: [boring.x, boring.y], to: [v.x, v.y] });
      patch.x = v.x; patch.y = v.y;
    }
    if (v.z != null && !sameNumber(boring.z, v.z, 1e-6)) {
      changes.push({ field: 'z', from: boring.z ?? null, to: v.z });
      patch.z = v.z;
    }
    if (v.datum && v.datum !== (boring.datum || '').slice(0, 10)) {
      changes.push({ field: 'datum', from: boring.datum ?? null, to: v.datum });
      patch.datum = v.datum;
    }
    if (v.boormeester && v.boormeester !== (boring.boormeester || '')) {
      changes.push({ field: 'boormeester', from: boring.boormeester ?? null, to: v.boormeester });
      patch.boormeester = v.boormeester;
    }
    return { ...r, boring, changes, patch };
  }).sort((a, b) => a.boring.name.localeCompare(b.boring.name, 'nl', { numeric: true, sensitivity: 'base' }));

  const touched = new Set(rows.map(r => r.boringId).filter(Boolean));
  const ambiguousIds = new Set(rows.filter(r => r.match.kind === 'ambiguous').flatMap(r => r.match.borings.map(b => b.id)));
  return {
    entries,
    payload: entries.filter(e => Object.keys(e.patch).length).map(e => ({ boringId: e.boring.id, ...e.patch })),
    unmatched: rows.filter(r => r.match.kind === 'none'),
    ambiguous: rows.filter(r => r.match.kind === 'ambiguous'),
    duplicates,
    missing: borings.filter(b => !touched.has(b.id) && !ambiguousIds.has(b.id)),
  };
}

/** Wijkt de `Job:`-code af van de projectcode? Spaties en hoofdletters tellen niet. */
export function jobCodeMismatch(jobCode, projectCode) {
  if (!jobCode || !projectCode) return false;
  const k = (s) => String(s).toLowerCase().replace(/\s+/g, '');
  return k(jobCode) !== k(projectCode);
}
